import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_SUBMITTED_QUESTIONS_API = process.env.REACT_APP_ADMIN_SUBMITTED_QUESTIONS_API || `${API_BASE_URL}/api/admin-app/submitted-questions/`;
const ADMIN_PUBLISHED_QUESTIONS_API = process.env.REACT_APP_ADMIN_PUBLISHED_QUESTIONS_API || `${API_BASE_URL}/api/admin-app/published-questions/`;
const ADMIN_APTITUDE_QUESTIONS_API = process.env.REACT_APP_ADMIN_APTITUDE_QUESTIONS_API || `${API_BASE_URL}/api/admin-app/aptitude-questions/`;
const ADMIN_TEST_HISTORY_API = process.env.REACT_APP_ADMIN_TEST_HISTORY_API || `${API_BASE_URL}/api/admin-app/test-history/`;

const AdminSubmittedQuestionsPage = () => {
    const [submitted, setSubmitted] = useState([]);
    const [published, setPublished] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTestForm, setShowTestForm] = useState(false);
    const [testForm, setTestForm] = useState({ question_count: '', difficulty: '' });
    const [testSubmitting, setTestSubmitting] = useState(false);
    const [testMessage, setTestMessage] = useState('');
    const [testError, setTestError] = useState('');

    const headers = useMemo(() => {
        // Try guest_access_token first (for backward compatibility)
        let token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
        
        // If not found, try admin userDetails (from redux-based login)
        if (!token) {
            const userDetailsStr = localStorage.getItem('userDetails');
            if (userDetailsStr) {
                try {
                    const userDetails = JSON.parse(userDetailsStr);
                    token = userDetails.access || '';
                } catch (e) {
                    // JSON parse error, token remains empty
                }
            }
        }
        
        if (!token) {
            return null;
        }

        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }, []);

    const load = async () => {
        if (!headers) {
            setLoading(false);
            return;
        }

        try {
            const [submittedRes, publishedRes] = await Promise.all([
                fetch(ADMIN_SUBMITTED_QUESTIONS_API, { headers }),
                fetch(ADMIN_PUBLISHED_QUESTIONS_API, { headers }),
            ]);
            const submittedData = submittedRes.ok ? await submittedRes.json() : [];
            const publishedData = publishedRes.ok ? await publishedRes.json() : [];
            setSubmitted(Array.isArray(submittedData) ? submittedData : []);
            setPublished(Array.isArray(publishedData) ? publishedData : []);
        } catch (error) {
            setSubmitted([]);
            setPublished([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [headers]);

    const handlePublish = async (row) => {
        if (!headers) {
            return;
        }

        try {
            const response = await fetch(`${ADMIN_APTITUDE_QUESTIONS_API}${row.id}/`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({ status: 'published' }),
            });

            if (!response.ok) {
                return;
            }

            setSubmitted((prev) => prev.filter((item) => item.id !== row.id));
            setPublished((prev) => [
                { ...row, status: 'published' },
                ...prev,
            ]);
        } catch (error) {
            // keep silent for now
        }
    };

    const navigate = useNavigate();

    const handleOpenTestForm = () => {
        // navigate to dedicated create test page
        navigate('/admin/create-test?role=admin');
    };

    const handlePublishAll = async () => {
        if (!headers) return;

        const result = await Swal.fire({
            title: 'Publish all submitted questions?',
            text: 'This will publish all submitted questions and make them live in the test pool.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, publish all',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        if (!submitted || submitted.length === 0) {
            await Swal.fire({ icon: 'info', title: 'No submitted questions', text: 'No submitted questions found.' });
            return;
        }

        // publish in parallel
        const ops = submitted.map((row) =>
            fetch(`${ADMIN_APTITUDE_QUESTIONS_API}${row.id}/`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({ status: 'published' }),
            }).then((res) => ({ ok: res.ok, id: row.id, row }))
                .catch(() => ({ ok: false, id: row.id, row }))
        );

        const results = await Promise.all(ops);

        let successCount = 0;
        const failed = [];

        results.forEach((r) => {
            if (r.ok) {
                successCount += 1;
            } else {
                failed.push(r.row);
            }
        });

        // Update UI: move successful ones to published and keep failed in submitted
        const succeededIds = results.filter(r => r.ok).map(r => r.id);
        const newlyPublished = submitted.filter(s => succeededIds.includes(s.id)).map(s => ({ ...s, status: 'published' }));
        const stillSubmitted = submitted.filter(s => !succeededIds.includes(s.id));

        setPublished((prev) => [...newlyPublished, ...prev]);
        setSubmitted(stillSubmitted);

        // SweetAlert summary
        if (failed.length === 0) {
            await Swal.fire({ icon: 'success', title: 'Published', text: `Published ${successCount} questions successfully.` });
        } else {
            await Swal.fire({ icon: 'warning', title: 'Partial publish', text: `Published ${successCount} questions. ${failed.length} failed.` });
        }
    };

    const handleCloseTestForm = () => {
        setShowTestForm(false);
        setTestError('');
        setTestMessage('');
    };

    const handleTestInputChange = (event) => {
        const { name, value } = event.target;
        setTestForm((prev) => ({ ...prev, [name]: value }));
        if (testError) setTestError('');
        if (testMessage) setTestMessage('');
    };

    const handleCreateTest = async (event) => {
        event.preventDefault();
        if (!headers) {
            return;
        }

        const questionCount = Number(testForm.question_count);
        if (!questionCount || Number.isNaN(questionCount) || questionCount < 1) {
            setTestError('Enter a valid question count.');
            return;
        }

        const payload = {
            question_count: questionCount,
        };

        if (testForm.difficulty) {
            payload.difficulty = testForm.difficulty;
        }

        setTestSubmitting(true);
        try {
            const response = await fetch(ADMIN_TEST_HISTORY_API, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setTestError(data?.error || 'Unable to create test right now.');
                return;
            }

            setTestMessage('Test created successfully.');
            setShowTestForm(false);
        } catch (error) {
            setTestError('Unable to create test right now.');
        } finally {
            setTestSubmitting(false);
        }
    };

    return (
        <div>
            {showTestForm ? (
                <div className="admin-top-sheet-backdrop" onClick={handleCloseTestForm}>
                    <form
                        className="admin-college-form admin-top-sheet"
                        onSubmit={handleCreateTest}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="admin-top-sheet-header">
                            <h4 className="admin-college-dropdown-title">Create Test</h4>
                            <button
                                type="button"
                                className="admin-top-sheet-close"
                                onClick={handleCloseTestForm}
                                disabled={testSubmitting}
                                aria-label="Close create test form"
                            >
                                x
                            </button>
                        </div>

                        <div className="admin-college-form-grid">
                            <div>
                                <label className="admin-security-label" htmlFor="question_count">Question Count</label>
                                <input
                                    id="question_count"
                                    name="question_count"
                                    type="number"
                                    min="1"
                                    className="admin-security-input"
                                    value={testForm.question_count}
                                    onChange={handleTestInputChange}
                                    disabled={testSubmitting}
                                />
                            </div>
                            <div>
                                <label className="admin-security-label" htmlFor="difficulty">Difficulty Mode</label>
                                <select
                                    id="difficulty"
                                    name="difficulty"
                                    className="admin-security-input"
                                    value={testForm.difficulty}
                                    onChange={handleTestInputChange}
                                    disabled={testSubmitting}
                                >
                                    <option value="">All</option>
                                    <option value="Easy">Easy</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Hard">Hard</option>
                                </select>
                            </div>
                        </div>

                        {testError ? <div className="admin-security-feedback admin-security-feedback-error">{testError}</div> : null}
                        {testMessage ? <div className="admin-security-feedback admin-security-feedback-success">{testMessage}</div> : null}

                        <div className="admin-college-actions">
                            <button
                                type="submit"
                                className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn"
                                disabled={testSubmitting}
                            >
                                {testSubmitting ? 'Creating...' : 'Create Test'}
                            </button>
                            <button
                                type="button"
                                className="admin-profile-action-btn"
                                onClick={handleCloseTestForm}
                                disabled={testSubmitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}

            <div className="admin-panel-card admin-table-section">
                <div className="admin-college-header">
                    <div>
                        <h3>Submitted Questions</h3>
                        <p className="admin-subtitle">Questions awaiting publish review.</p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            type="button"
                            className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn"
                            onClick={handleOpenTestForm}
                            disabled={testSubmitting}
                        >
                            Create Test
                        </button>
                        <button
                            type="button"
                            className="admin-profile-action-btn admin-profile-action-btn-primary"
                            onClick={async () => await handlePublishAll()}
                            disabled={loading || submitted.length === 0}
                            title={submitted.length === 0 ? 'No submitted questions to publish' : 'Publish all submitted questions'}
                        >
                            Publish All
                        </button>
                    </div>
                </div>
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Topic</th>
                                <th>Difficulty</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5">Loading submitted questions...</td></tr>
                            ) : submitted.length === 0 ? (
                                <tr><td colSpan="5">No submitted questions found.</td></tr>
                            ) : (
                                submitted.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.id}</td>
                                        <td>{row.topic || '-'}</td>
                                        <td>{row.difficulty || '-'}</td>
                                        <td>{row.status || '-'}</td>
                                        <td>
                                            <div className="admin-row-actions">
                                                <button
                                                    type="button"
                                                    className="admin-row-action-btn"
                                                    onClick={() => handlePublish(row)}
                                                >
                                                    Publish
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <PublishedQuestionsSection loading={loading} published={published} />
        </div>
    );
};


const PublishedQuestionsSection = ({ loading, published }) => {
    const [selectedTopic, setSelectedTopic] = useState(null);

    // Group questions by topic and calculate totals
    const topicStats = useMemo(() => {
        const grouped = {};
        published.forEach((question) => {
            const topic = question.topic_name || 'Uncategorized';
            if (!grouped[topic]) {
                grouped[topic] = {
                    topic,
                    totalQuestions: 0,
                    totalMarks: 0,
                    questions: [],
                };
            }
            grouped[topic].totalQuestions += 1;
            grouped[topic].totalMarks += (question.marks ?? 0);
            grouped[topic].questions.push(question);
        });

        return Object.values(grouped).sort((a, b) => a.topic.localeCompare(b.topic));
    }, [published]);

    const filteredStats = selectedTopic 
        ? topicStats.filter(stat => stat.topic === selectedTopic)
        : topicStats;

    return (
        <div className="admin-panel-card admin-table-section">
            <div className="admin-college-header">
                <div>
                    <h3>Published Questions</h3>
                    <p className="admin-subtitle">Questions already available in test pool.</p>
                </div>
            </div>

            {!loading && topicStats.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="topic-filter" style={{ marginRight: '10px', fontWeight: '500' }}>
                        Filter by Topic:
                    </label>
                    <select
                        id="topic-filter"
                        className="admin-security-input"
                        value={selectedTopic || ''}
                        onChange={(e) => setSelectedTopic(e.target.value || null)}
                        style={{ maxWidth: '300px' }}
                    >
                        <option value="">All Topics ({topicStats.reduce((sum, s) => sum + s.totalQuestions, 0)} questions)</option>
                        {topicStats.map((stat) => (
                            <option key={stat.topic} value={stat.topic}>
                                {stat.topic} ({stat.totalQuestions} questions, {stat.totalMarks} marks)
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="admin-table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Topic</th>
                            <th>Total Questions</th>
                            <th>Total Marks</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="3">Loading published questions...</td></tr>
                        ) : filteredStats.length === 0 ? (
                            <tr><td colSpan="3">No published questions found.</td></tr>
                        ) : (
                            filteredStats.map((stat) => (
                                <tr key={stat.topic}>
                                    <td><strong>{stat.topic}</strong></td>
                                    <td>{stat.totalQuestions}</td>
                                    <td>{stat.totalMarks}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminSubmittedQuestionsPage;
